import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useAdminGetStripeSubscriptions } from '@/api/auth/admin/admin';
import { StripeSubscriptionResponse } from '@/api/auth/missionImpactAuthAPI.schemas';

/**
 * Expandable row component for displaying subscription with its line items.
 */
function SubscriptionRow({
  subscription,
  formatCurrency,
  formatDate,
  getStatusBadge,
}: {
  subscription: StripeSubscriptionResponse;
  formatCurrency: (amount: number | undefined, currency: string | undefined) => string;
  formatDate: (dateString: string | undefined) => string;
  getStatusBadge: (status: string | undefined) => React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasLineItems = subscription.lineItems && subscription.lineItems.length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <>
        <TableRow className={hasLineItems ? 'cursor-pointer' : ''}>
          <TableCell className="w-8">
            {hasLineItems && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <ChevronRight
                    className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
            )}
          </TableCell>
          <TableCell className="font-mono text-sm">
            {subscription.stripeSubscriptionId}
          </TableCell>
          <TableCell className="font-mono text-sm">
            {subscription.stripeCustomerId}
          </TableCell>
          <TableCell>{getStatusBadge(subscription.status)}</TableCell>
          <TableCell>
            {formatCurrency(subscription.amount, subscription.currency)}
          </TableCell>
          <TableCell>{subscription.lineItems?.length || 0}</TableCell>
          <TableCell className="text-muted-foreground text-sm">
            {formatDate(subscription.createdAt)}
          </TableCell>
        </TableRow>
        {hasLineItems && (
          <CollapsibleContent asChild>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableCell colSpan={7} className="p-0">
                <div className="px-8 py-3">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Item ID</TableHead>
                        <TableHead className="text-xs">Price ID</TableHead>
                        <TableHead className="text-xs">Product ID</TableHead>
                        <TableHead className="text-xs">Quantity</TableHead>
                        <TableHead className="text-xs">Period</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscription.lineItems?.map((item) => (
                        <TableRow key={item.stripeSubscriptionItemId}>
                          <TableCell className="font-mono text-xs">
                            {item.stripeSubscriptionItemId}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {item.stripePriceId}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {item.stripeProductId}
                          </TableCell>
                          <TableCell className="text-xs">{item.quantity}</TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {formatDate(item.currentPeriodStart)} -{' '}
                            {formatDate(item.currentPeriodEnd)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TableCell>
            </TableRow>
          </CollapsibleContent>
        )}
      </>
    </Collapsible>
  );
}

/**
 * Admin page for viewing Stripe subscriptions.
 * Note: Subscriptions are synced as part of customer sync from the Customers page.
 */
export default function StripeSubscriptionsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const {
    data: subscriptionsData,
    isLoading,
    error,
  } = useAdminGetStripeSubscriptions({ page, pageSize });

  const subscriptions = subscriptionsData?.data?.items || [];
  const totalCount = subscriptionsData?.data?.totalCount || 0;
  const hasMore = subscriptionsData?.data?.hasMore || false;

  const formatCurrency = (amount: number | undefined, currency: string | undefined) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'past_due':
      case 'pastdue':
        return <Badge variant="destructive">Past Due</Badge>;
      case 'canceled':
        return <Badge variant="secondary">Canceled</Badge>;
      case 'unpaid':
        return <Badge variant="destructive">Unpaid</Badge>;
      default:
        return <Badge variant="outline">{status || 'Unknown'}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Stripe Subscriptions</h1>
          <p className="text-muted-foreground">
            View synced Stripe subscriptions ({totalCount} total). Subscriptions are synced via the Customers page.
          </p>
        </div>
      </div>

      {error ? (
        <div className="text-center text-destructive py-8">
          <p>Failed to load subscriptions: {(error as Error)?.message || 'Unknown error'}</p>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Subscription ID</TableHead>
                <TableHead>Customer ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Line Items</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[180px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[180px]" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[40px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                  </TableRow>
                ))
              ) : subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No subscriptions found. Sync customers from the Customers page to import subscriptions.
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map((subscription) => (
                  <SubscriptionRow
                    key={subscription.stripeSubscriptionId}
                    subscription={subscription}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                    getStatusBadge={getStatusBadge}
                  />
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalCount > pageSize && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="py-2 px-3 text-sm text-muted-foreground">
                Page {page}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
